/**
 * Industry-Specific Wording System
 * 
 * Provides industry-appropriate labels for UI elements
 * Makes the CRM feel native to each industry
 */

export interface IndustryWording {
  // Entity Names (plural)
  leads: string;
  customers: string;
  opportunities: string;
  products: string;
  
  // Entity Names (singular)
  lead: string;
  customer: string;
  opportunity: string;
  product: string;
  
  // Product Categories
  productTypes: Array<{
    key: string;
    label: string;
    description: string;
  }>;
  
  // Pipeline Names
  stages: {
    lead: string;      // "Probefahrt-Pipeline"
    opportunity: string;  // "Verkaufs-Pipeline"
  };
  
  // Action Verbs
  actions: {
    createLead: string;     // "Neue Probefahrt-Anfrage"
    createCustomer: string; // "Neuer Kunde"
    createProduct: string;  // "Neues Fahrzeug"
  };
  
  // Dashboard Widgets
  dashboard: {
    leadsWidget: string;        // "Probefahrt-Anfragen"
    customersWidget: string;    // "Kunden"
    revenueWidget: string;      // "Umsatz"
  };
}

// ============================================================================
// Industry Wording Definitions
// ============================================================================

export const INDUSTRY_WORDING: Record<string, IndustryWording> = {
  finance: {
    // Entity Names
    leads: 'Beratungsanfragen',
    customers: 'Bankkunden',
    opportunities: 'Finanzprodukte',
    products: 'Produktkategorien',
    
    lead: 'Beratungsanfrage',
    customer: 'Bankkunde',
    opportunity: 'Finanzprodukt',
    product: 'Produktkategorie',
    
    // Product Categories (specific to finance)
    productTypes: [
      {
        key: 'accounts',
        label: 'Konten',
        description: 'Girokonten, Sparkonten, Kreditkarten'
      },
      {
        key: 'loans',
        label: 'Kredite & Finanzierung',
        description: 'Privatkredit, Baufinanzierung, Autokredit'
      },
      {
        key: 'investments',
        label: 'Investment & Altersvorsorge',
        description: 'Depot, Investment-Beratung, Vermögensaufbau'
      }
    ],
    
    stages: {
      lead: 'Beratungs-Pipeline',
      opportunity: 'Produkt-Pipeline'
    },
    
    actions: {
      createLead: 'Neue Beratungsanfrage',
      createCustomer: 'Neuer Bankkunde',
      createProduct: 'Neues Finanzprodukt'
    },
    
    dashboard: {
      leadsWidget: 'Beratungsanfragen',
      customersWidget: 'Bankkunden',
      revenueWidget: 'Produkt-Umsatz'
    }
  },
  
  agency: {
    // Entity Names
    leads: 'Anfragen',
    customers: 'Klienten',
    opportunities: 'Projekte',
    products: 'Services',
    
    lead: 'Anfrage',
    customer: 'Klient',
    opportunity: 'Projekt',
    product: 'Service',
    
    // Product Categories (specific to marketing agency)
    productTypes: [
      {
        key: 'marketing',
        label: 'Marketing Services',
        description: 'Social Media, SEO, Content, Ads'
      },
      {
        key: 'creative',
        label: 'Creative Services',
        description: 'Design, Branding, Web Design'
      },
      {
        key: 'performance',
        label: 'Performance Marketing',
        description: 'Google Ads, Facebook Ads, Analytics'
      }
    ],
    
    stages: {
      lead: 'Anfragen-Pipeline',
      opportunity: 'Projekt-Pipeline'
    },
    
    actions: {
      createLead: 'Neue Anfrage',
      createCustomer: 'Neuer Klient',
      createProduct: 'Neuer Service'
    },
    
    dashboard: {
      leadsWidget: 'Neue Anfragen',
      customersWidget: 'Klienten',
      revenueWidget: 'Klienten-Umsatz'
    }
  },
  
  ecommerce: {
    // Entity Names
    leads: 'Kundenanfragen',
    customers: 'Kunden',
    opportunities: 'Bestellungen',
    products: 'Interaktionstypen',
    
    lead: 'Kundenanfrage',
    customer: 'Kunde',
    opportunity: 'Bestellung',
    product: 'Interaktionstyp',
    
    // Product Categories (specific to e-commerce CRM)
    productTypes: [
      {
        key: 'interactions',
        label: 'Kundeninteraktionen',
        description: 'Bestellungen, Anfragen, Support, Retouren'
      },
      {
        key: 'customer_types',
        label: 'Kundentypen',
        description: 'Standard, VIP, B2B-Kunde'
      }
    ],
    
    stages: {
      lead: 'Anfragen-Pipeline',
      opportunity: 'Bestell-Pipeline'
    },
    
    actions: {
      createLead: 'Neue Kundenanfrage',
      createCustomer: 'Neuer Kunde',
      createProduct: 'Neuer Interaktionstyp'
    },
    
    dashboard: {
      leadsWidget: 'Kundenanfragen',
      customersWidget: 'Kunden',
      revenueWidget: 'Shop-Umsatz'
    }
  },
  
  consulting: {
    // Entity Names
    leads: 'Leads',
    customers: 'Kunden',
    opportunities: 'Projekte',
    products: 'Dienstleistungen',
    
    lead: 'Lead',
    customer: 'Kunde',
    opportunity: 'Projekt',
    product: 'Dienstleistung',
    
    // Product Categories (specific to consulting)
    productTypes: [
      {
        key: 'consultancy',
        label: 'Beratung',
        description: 'Strategy, Business, IT, Management Consulting'
      },
      {
        key: 'services',
        label: 'Services',
        description: 'Project Management, Training, Interim'
      },
      {
        key: 'audit',
        label: 'Audit & Analyse',
        description: 'Audits, Analysen, Assessments'
      }
    ],
    
    stages: {
      lead: 'Lead-Pipeline',
      opportunity: 'Projekt-Pipeline'
    },
    
    actions: {
      createLead: 'Neuer Lead',
      createCustomer: 'Neuer Kunde',
      createProduct: 'Neue Dienstleistung'
    },
    
    dashboard: {
      leadsWidget: 'Leads',
      customersWidget: 'Kunden',
      revenueWidget: 'Projekt-Umsatz'
    }
  },
  
  automotive: {
    // Entity Names
    leads: 'Probefahrt-Anfragen',
    customers: 'Kunden',
    opportunities: 'Verkaufs-Chancen',
    products: 'Fahrzeuge & Services',
    
    lead: 'Probefahrt-Anfrage',
    customer: 'Kunde',
    opportunity: 'Verkaufs-Chance',
    product: 'Fahrzeug/Service',
    
    // Product Categories (specific to automotive)
    productTypes: [
      {
        key: 'vehicles',
        label: 'Fahrzeuge',
        description: 'Neu- und Gebrauchtwagen, Leasing'
      },
      {
        key: 'service',
        label: 'Service & Werkstatt',
        description: 'Inspektionen, Reparaturen, Wartung'
      },
      {
        key: 'parts',
        label: 'Teile & Zubehör',
        description: 'Original-Teile, Aftermarket, Zubehör'
      },
      {
        key: 'financing',
        label: 'Finanzierung & Leasing',
        description: 'Finanzierungs-Optionen und Leasing-Angebote'
      }
    ],
    
    stages: {
      lead: 'Probefahrt-Pipeline',
      opportunity: 'Verkaufs-Pipeline'
    },
    
    actions: {
      createLead: 'Neue Probefahrt-Anfrage',
      createCustomer: 'Neuer Kunde',
      createProduct: 'Neues Fahrzeug/Service'
    },
    
    dashboard: {
      leadsWidget: 'Probefahrt-Anfragen',
      customersWidget: 'Kunden',
      revenueWidget: 'Verkaufs-Umsatz'
    }
  },
  
  insurance: {
    // Entity Names
    leads: 'Anfragen',
    customers: 'Versicherte',
    opportunities: 'Policen-Chancen',
    products: 'Versicherungstypen',
    
    lead: 'Anfrage',
    customer: 'Versicherter',
    opportunity: 'Policen-Chance',
    product: 'Versicherungstyp',
    
    // Product Categories (specific to insurance)
    productTypes: [
      {
        key: 'insurance',
        label: 'Versicherungen',
        description: 'Alle Versicherungstypen'
      },
      {
        key: 'private',
        label: 'Privat-Versicherungen',
        description: 'KFZ, Hausrat, Leben, Kranken'
      },
      {
        key: 'business',
        label: 'Gewerbe-Versicherungen',
        description: 'Betriebshaftpflicht, Geschäftsversicherungen'
      }
    ],
    
    stages: {
      lead: 'Angebots-Pipeline',
      opportunity: 'Policen-Pipeline'
    },
    
    actions: {
      createLead: 'Neue Anfrage',
      createCustomer: 'Neuer Versicherter',
      createProduct: 'Neue Versicherung'
    },
    
    dashboard: {
      leadsWidget: 'Neue Anfragen',
      customersWidget: 'Versicherte',
      revenueWidget: 'Prämien-Umsatz'
    }
  },
  
  saas: {
    // Entity Names
    leads: 'Trial-User',
    customers: 'Abonnenten',
    opportunities: 'Upgrade-Chancen',
    products: 'Subscriptions',
    
    lead: 'Trial-User',
    customer: 'Abonnent',
    opportunity: 'Upgrade-Chance',
    product: 'Subscription',
    
    // Product Categories (specific to SaaS)
    productTypes: [
      {
        key: 'subscriptions',
        label: 'Subscription-Pläne',
        description: 'Starter, Pro, Business, Enterprise'
      },
      {
        key: 'addons',
        label: 'Add-ons',
        description: 'API-Zugang, Premium Support'
      },
      {
        key: 'services',
        label: 'Services',
        description: 'Integration, Training, Consulting'
      }
    ],
    
    stages: {
      lead: 'Trial-Pipeline',
      opportunity: 'Subscription-Pipeline'
    },
    
    actions: {
      createLead: 'Neuer Trial-User',
      createCustomer: 'Neuer Abonnent',
      createProduct: 'Neues Subscription-Paket'
    },
    
    dashboard: {
      leadsWidget: 'Trial-User',
      customersWidget: 'Abonnenten',
      revenueWidget: 'MRR/ARR'
    }
  },
  
  // Default/Fallback for organizations without specific industry
  default: {
    leads: 'Leads',
    customers: 'Kunden',
    opportunities: 'Opportunities',
    products: 'Produkte & Services',
    
    lead: 'Lead',
    customer: 'Kunde',
    opportunity: 'Opportunity',
    product: 'Produkt',
    
    productTypes: [
      {
        key: 'consultancy',
        label: 'Beratung',
        description: 'Consulting-Dienstleistungen'
      },
      {
        key: 'development',
        label: 'Entwicklung',
        description: 'Software-Entwicklung, Projekte'
      },
      {
        key: 'solution',
        label: 'Lösungen',
        description: 'Fertige Lösungen und Produkte'
      },
      {
        key: 'service',
        label: 'Services',
        description: 'Dienstleistungen'
      }
    ],
    
    stages: {
      lead: 'Lead-Pipeline',
      opportunity: 'Verkaufs-Pipeline'
    },
    
    actions: {
      createLead: 'Neuer Lead',
      createCustomer: 'Neuer Kunde',
      createProduct: 'Neues Produkt'
    },
    
    dashboard: {
      leadsWidget: 'Leads',
      customersWidget: 'Kunden',
      revenueWidget: 'Umsatz'
    }
  }
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get wording for an industry
 */
export function getIndustryWording(industry?: string): IndustryWording {
  if (!industry) return INDUSTRY_WORDING.default;
  return INDUSTRY_WORDING[industry] || INDUSTRY_WORDING.default;
}

/**
 * Get product types for an industry
 */
export function getProductTypesForIndustry(industry?: string): Array<{
  key: string;
  label: string;
  description: string;
}> {
  const wording = getIndustryWording(industry);
  return wording.productTypes;
}

/**
 * Get entity label (plural)
 */
export function getEntityLabel(
  entity: 'leads' | 'customers' | 'opportunities' | 'products',
  industry?: string
): string {
  const wording = getIndustryWording(industry);
  return wording[entity];
}

/**
 * Get entity label (singular)
 */
export function getEntityLabelSingular(
  entity: 'lead' | 'customer' | 'opportunity' | 'product',
  industry?: string
): string {
  const wording = getIndustryWording(industry);
  return wording[entity];
}

/**
 * Get action label
 */
export function getActionLabel(
  action: 'createLead' | 'createCustomer' | 'createProduct',
  industry?: string
): string {
  const wording = getIndustryWording(industry);
  return wording.actions[action];
}

/**
 * Get dashboard widget label
 */
export function getDashboardLabel(
  widget: 'leadsWidget' | 'customersWidget' | 'revenueWidget',
  industry?: string
): string {
  const wording = getIndustryWording(industry);
  return wording.dashboard[widget];
}

